<template>
  <div class="d-flex align-items-center bg-auth border-top border-top-2 border-primary min-vh-100">
    <div class="onboarding-container container">
      <div class="row align-items-center min-vh-100">
        <div class="col-12 col-md-6 offset-md-1 order-md-2 mb-5 mb-md-0">
          <!-- Heading -->
          <h1 class="display-4 text-center mb-3">
            Welcome!
          </h1>
          <!-- Subheading -->
          <p class="text-muted text-center mb-5">
            Thank you for signing up, please fill out the form below to kick-start the onboarding process.
          </p>

          <!-- Step Progress -->
          <div class="d-flex justify-content-center mb-4">
            <div class="d-flex align-items-center">
              <div class="step-circle" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                <i class="fe fe-user" v-if="currentStep === 1"></i>
                <i class="fe fe-check" v-else-if="currentStep > 1"></i>
                <span v-else>1</span>
              </div>
              <div class="step-line" :class="{ active: currentStep > 1 }"></div>
              <div class="step-circle" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                <i class="fe fe-cloud" v-if="currentStep === 2"></i>
                <i class="fe fe-check" v-else-if="currentStep > 2"></i>
                <span v-else>2</span>
              </div>
              <div class="step-line" :class="{ active: currentStep > 2 }"></div>
              <div class="step-circle" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
                <i class="fe fe-settings" v-if="currentStep === 3"></i>
                <i class="fe fe-check" v-else-if="currentStep > 3"></i>
                <span v-else>3</span>
              </div>
            </div>
          </div>

          <!-- Step Labels -->
          <div class="d-flex justify-content-center mb-4">
            <div class="row text-center w-100">
              <div class="col-4">
                <small class="text-muted">Personal Information</small>
              </div>
              <div class="col-4">
                <small class="text-muted">SKU {{editable?"Selection":"Selected"}}</small>
              </div>
              <div class="col-4">
                <small class="text-muted">Configuration</small>
              </div>
            </div>
          </div>

          <!-- Error Messages -->
          <div v-if="errorMessage" class="alert alert-danger mb-4">
            {{ errorMessage }}
          </div>

          <!-- Loading State -->
          <div v-if="isSubmitting" class="text-center">
            <div class="mb-4">
              <i class="fe fe-loader spin text-primary" style="font-size: 4rem;"></i>
            </div>
            <h3 class="mb-3">Processing Your Request...</h3>
            <p class="text-muted mb-4">
              Please wait while we set up your account. This may take a few moments.
            </p>
            <div class="progress" style="height: 4px;">
              <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                style="width: 100%">
              </div>
            </div>
          </div>

          <!-- Success State -->
          <div v-else-if="isSubmitted" class="text-center">
            <div class="mb-4">
              <i class="fe fe-check-circle text-success" style="font-size: 4rem;"></i>
            </div>
            <h3 class="mb-3">Thank You!</h3>
            <p class="text-muted mb-4">
              Your onboarding request has been successfully submitted. You will receive an email notification 
              when the automated onboarding and deployment process is completed.
            </p>
            <p class="text-muted mb-4">
              Once you receive the confirmation email, you'll be able to log in with your provided email address 
              (<strong>{{ formData.email }}</strong>) to access your account.
            </p>
            <a href="/login" class="btn btn-primary btn-lg">
              <i class="fe fe-log-in"></i> Go to Login Page
            </a>
          </div>

          <!-- Form -->
          <form v-else @submit.prevent="submitForm">

            <!-- Step 1: Personal Information -->
            <div v-if="currentStep === 1" class="step-content">
              <h5 class="mb-4">Step 1: Personal Information</h5>

              <div class="form-group">
                <InputField type="radio" name="existingCustomer" label="Existing Customer" :inlineField="true"
                  toolTip="Are you an existing customer?" :options="existingCustOption" v-model="existCust" />
              </div>
              <div class="form-group">
                <InputField v-if="existCust" type="text" name="cid" label="Customer ID (CID)" placeholder="eg: c001"
                  toolTip="Enter already registered CID eg:c001" v-model="cid"
                  :validate="[{ type: 'required', errMsg: 'cid is required' }]" />
              </div>
              <div class="form-group">
                <InputField type="text" name="enail" label="Organization Name" placeholder="Your Organization Name"
                  :toolTip='existCust ? "Enter the registered organization name." : "Enter the organization name"'
                  v-model="orgName" :validate="[{ type: 'required', errMsg: 'organization name is required' }]" />
              </div>
              <!-- Email address -->
              <div class="form-group">
                <InputField type="text" name="enail" label="Email Address" placeholder="name@address.com"
                  toolTip="Enter a valid email address. This will be used for account login and notifications."
                  v-model="formData.email" :validate="[{ type: 'required', errMsg: 'email is required' },
                  { type: 'regex', pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$', errMsg: 'enter valid email address' }
                  ]" />
              </div>
            </div>

            <!-- Step 2: SKU Selection -->
            <div v-if="currentStep === 2" class="step-content mb-3">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h5 class="mb-0">Step 2: SKU {{editable?"Selection":"Selected"}}</h5>
                <a href="https://help.aqfer.net/en_US/aqfer-onboarding-cloud-services/onboarding-guidelines" target="_blank" rel="noopener" class="small text-primary text-nowrap">
                  <i class="fe fe-help-circle me-1"></i>View onboarding guidelines
                </a>
              </div>

              <!-- SKU Selection -->
              <div class="form-group">
                <InputField type="select" name="sku" label="SKU" :options="skuOptions" v-model="formData.sku"
                  :editable="editable" />
              </div>

              <!-- SKU-specific Help Link -->
              <div v-if="formData.sku === 'aUT10'" class="mb-2">
                <a href="https://help.aqfer.net/en_US/aqfer-onboarding-cloud-services/aut10" target="_blank" rel="noopener" class="small text-info">
                  <i class="fe fe-info me-1"></i>Learn more about aUT10 setup and features
                </a>
              </div>
              <div v-else-if="formData.sku === 'aUT11'" class="mb-2">
                <a href="https://help.aqfer.net/en_US/aqfer-onboarding-cloud-services/aut11" target="_blank" rel="noopener" class="small text-info">
                  <i class="fe fe-info me-1"></i>Learn more about aUT11 setup and features
                </a>
              </div>
              <div v-else-if="formData.sku === 'aUT12'" class="mb-2">
                <a href="https://help.aqfer.net/en_US/aqfer-onboarding-cloud-services/aut12" target="_blank" rel="noopener" class="small text-info">
                  <i class="fe fe-info me-1"></i>Learn more about aUT12 setup and features
                </a>
              </div>

              <!-- SKU Features Display -->
              <div v-if="formData.sku">
                <div class="card border-0 bg-light mb-3">
                  <div class="card-body">
                    <h4 class="card-title">{{ formData.sku }} SKU Features</h4>
                    <div v-html="features"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: SKU Configuration -->
            <div v-if="currentStep === 3" class="step-content mb-3">
              <h5 class="mb-4">Step 3: SKU Configuration</h5>

              <!-- Show selected sku info -->
              <div v-if="formData.sku" class="alert alert-light mb-4">
                <i class="fe fe-info-circle me-2"></i>
                <strong>Selected SKU:</strong> {{ formData.sku }}
              </div>
              <!-- Instructions Accordion - Only show when sku is selected -->
              <div v-if="formData.sku && !noTemplate" class="accordion mb-2" id="instructionsAccordion">
                <div class="accordion-item">
                  <h2 class="accordion-header" id="instructionsHeading">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse"
                      data-bs-target="#instructionsCollapse" aria-expanded="true" aria-controls="instructionsCollapse">
                      <i class="fe fe-info me-2"></i>
                      Setup Instructions
                      </button>
                  </h2>
                  <div id="instructionsCollapse" class="accordion-collapse collapse show"
                    aria-labelledby="instructionsHeading" data-bs-parent="#instructionsAccordion">
                    <div class="accordion-body">
                      <div class="alert alert-info-bg mb-0">
                        <div v-html="instructions"></div>
                        <div v-if="templateLoading" class="spinner-border spinner-border-sm" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <div v-if="templateUrl" class="mt-3 pt-3 border-top">
                          <div class="d-flex flex-wrap gap-2">
                            <div>
                                <a v-if="formData.sku_info.region" target="_blank" :href="templateRedirectUrl"><i class="fa fa-external-link me-1"></i></a> &nbsp;
                                CloudFormation Template &nbsp;
                                <span v-if="formData.sku_info.region" @click="onCopyCFM">
                                  <i class='fa fa-copy' />                  
                                </span> &nbsp;
                                <span @click="viewCFMTemplate(formData.sku)">
                                  <i class="fe fe-eye me-1" />
                                </span>
                            </div>
                            </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="noTemplate">
                <p>No CFM template for {{formData.sku}} SKU</p>
              </div>
              <!-- SKU Configuration Fields -->
              <div v-if="skuFields.length > 0" class="sku-config-fields"
                :class="{ 'two-columns': skuFields.length > 3 }">
                <FormFields :fields="skuFields" v-model="formData.sku_info" />
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="d-flex justify-content-between mb-3">
              <button v-if="currentStep > 1" type="button" class="btn btn-outline-secondary" @click="previousStep">
                <i class="fe fe-arrow-left"></i> Previous
              </button>
              <div v-else></div>

              <button v-if="currentStep < 3" type="button" class="btn btn-primary" @click="nextStep"
                :disabled="!canProceedToNextStep">
                Next <i class="fe fe-arrow-right"></i>
              </button>

              <SubmitButton v-else :fields="skuFields" v-model="formData.sku_info" btnText="Start Onboarding"
                btnIcon="check" />

            </div>
          </form>
        </div>
        <div class="col-12 col-md-5 order-md-1 my-5 h-100">
          <!-- Image -->
          <div class="text-center">
            <img v-if="$store.state.darkMode" src="../assets/logo.png" alt="" class="img-fluid">
            <img v-else src="../assets/logo2.png" alt="" class="img-fluid">
            <div class="marketplace-logo mt-5">
            </div>
          </div>
        </div>
      </div>
    </div>
     <CFMTemplateModal :visible="showCFMModal" :sku="formData.sku" :template="currentTemplate"
      :loading="templateLoading" :error="templateError" @close="closeCFMModal" />
    </div>
</template>

<script>
import InputField from '../components/InputField.vue';
import FormFields from '../components/FormFields.vue';
import SubmitButton from '../components/SubmitButton.vue';
import axios from "axios";
import debounce from 'lodash/debounce';
import CFMTemplateModal from '../components/CFMTemplateModal.vue';
export default {
  name: "Onboarding",
  components: {
    InputField,
    FormFields,
    SubmitButton,
    CFMTemplateModal,
  },
  data() {
    return {
      currentStep: 1,
      isSubmitting: false,
      isSubmitted: false,
      errorMessage: '',
      existCust: false,
      cid: '',
      orgName: '',
      formData: {
        email: '',
        mkt_token: null,
        sku: null,
        sku_info: {}
      },
      skuFields: [],
      existingCustOption: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
      ],
      skuList: [],
      editable: true,
      templateUrl: null,
      templateRedirectUrl:null,
      fetchedTemplate: '',
      templateLoading: false,
      templateError: '',
      type: 'aqfer',
      noTemplate:false,
      showCFMModal: false,
    }
  },
  computed: {
    canProceedToNextStep() {
      var re = new RegExp("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$");
      if (this.currentStep === 1) {
        return this.formData.email && re.test(this.formData.email) &&
          (!this.existCust || (this.existCust && this.cid));
      } else if (this.currentStep === 2) {
        return this.formData.sku; // Must select a sku to proceed
      }
      return true;
    },
    skuOptions() {
      let options = [];
      this.skuList.map(item => {
        options.push({
          label: item.sku,
          value: item.sku
          })
      });
    return options;
    },
    features() {
      let features=this.skuList.find(p => p.sku === this.formData.sku)?.features || []
      features = features.map((feature=>" - "+feature))
      return features.join("<br/>")
    },
    instructions() {
      let instructions=this.skuList.find(p => p.sku === this.formData.sku)?.instructions || []
      instructions = instructions.map((instruction=>" - "+instruction))
      return instructions.join("<br/>")
    },
    currentTemplate() {
      return this.fetchedTemplate || '';
    }
  },
  mounted() {
    this.checkMarketplaceToken();
    this.initializeTooltips();
    this.fetchSKUList();
  },
  watch: {
    'formData.sku'(newSku, oldSku) {
      if (newSku !== oldSku) {
        this.fetchCFM(null);
        let fields = [];
        this.noTemplate = false;
        const initialSkuInfo = {};
        const selectedSku = this.skuList.find(item => item.sku === newSku);
        if (selectedSku) {
          selectedSku.fields.map((value, index) => {
            fields.push({
              name: value.name,
              type: value.values? "select" : value.type.toLowerCase(),
              options: !value.values?null: this.getOptions(value.values),
              label: value.name,
              editable: true,
              url: value.type.toLowerCase() == "url" ? "https://onetimesecret.com/" : null,
              toolTip: value.type.toLowerCase() == "url" ? "Do not directly enter your secret here. use the onetimesecret url generated using the 'key'" : 
                      value.name === 'bucket_prefix' ? "Enter a valid S3 bucket prefix (3-40 chars, lowercase letters/numbers/hyphens/periods, must start/end with letter/number, no consecutive periods)" : 
                      value.description || null,
              validation: value.required ? [{ type: "required" }] : []
            })
            if (value.type.toLowerCase() == 'url') {
              fields[index].type="text";
              fields[index].validation.push({
                type: "regex", pattern: `^https:\\/\\/(?:[a-zA-Z0-9-]+\\.)?onetimesecret\\.com\\/secret\\/(?:[a-zA-Z0-9-]+)*$`,
                errMsg: "Onetimesecret URL is required"
              })
            }
            if (value.name === 'bucket_prefix') {
              fields[index].validation.push({
                type: "regex", 
                pattern: "^(?!xn--|sthree-|amzn-s3-demo-)(?!.*\\.\\.)(?!\\d+\\.\\d+\\.\\d+\\.\\d+)[a-z0-9][a-z0-9.-]{1,38}[a-z0-9](?<!-s3alias)(?<!--ol-s3)(?<!\\.mrap)(?<!--x-s3)(?<!--table-s3)$",
                errMsg: "Invalid bucket prefix. Must be 3-40 characters, lowercase letters/numbers/periods/hyphens only, start/end with letter/number, no consecutive periods, cannot be IP address format, and must follow AWS S3 naming rules"
              })
              fields[index].defaultValue ='com.aqfer'
            }
            if (value.name === 'role_arn') {
              fields[index].validation.push({
                type: "regex",
                // Matches arn:partition:service:region:account-id:resource-id
                // Specifically for IAM Roles: arn:aws:iam::123456789012:role/RoleName
                pattern: "^arn:(?:aws|aws-us-gov|aws-cn):iam::\\d{12}:role\\/(?:[a-zA-Z0-9+=,.@\\-_]+\\/)*[a-zA-Z0-9+=,.@\\-_]+$",
                errMsg: "Invalid Role ARN. Must be in the format: arn:aws:iam::123456789012:role/RoleName"
              });
            }
            const account_id = sessionStorage.getItem('account_id');
             if (account_id && value.name === 'account_id') {
              initialSkuInfo['account_id'] = account_id;
              fields[index] ={ ...fields[index], value: account_id, editable: false };
            }else{
              // Use defaultValue if it exists, otherwise use empty string
              initialSkuInfo[value.name] = fields[index].defaultValue !== undefined ? fields[index].defaultValue : '';
            }
          })
          this.skuFields = fields;
          this.formData.sku_info = initialSkuInfo;
        } else {
          this.skuFields = [];
          this.formData.sku_info = {};
        }
      }
    },
    type(){
      this.fetchSKUList();
    },
    'formData.sku_info.region'(newRegion, oldRegion) {
      if (newRegion !="" && newRegion !== oldRegion) {
        this.debouncedFetchCFM(newRegion);
      }
    },
  },
  
  methods: {
    initializeTooltips() {
      // Initialize Bootstrap tooltips
      this.$nextTick(() => {
        if (window.$ && window.$.fn.tooltip) {
          window.$('[data-toggle="tooltip"]').tooltip();
        } else {
          // Fallback: Add title attribute for basic tooltip functionality
          document.querySelectorAll('[data-toggle="tooltip"]').forEach(element => {
            if (!element.hasAttribute('title')) {
              element.setAttribute('title', element.getAttribute('data-original-title') || '');
            }
          });
        }
      });
    },

    async checkMarketplaceToken() {
      // Get token from URL parameter, then clean the URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token') || urlParams.get('x-amzn-marketplace-token');
      const sku = urlParams.get('product_id');
      const account_id = urlParams.get('account_id');
      const loggedIn = await this.checkLoggedIn();
      this.type = 'aqfer';
      if (token) {
        // Store the token for form submission
        this.formData.mkt_token = token;
        this.formData.sku = sku;
        // Store in sessionStorage for persistence during the session
        sessionStorage.setItem('x-amzn-marketplace-token', token);
        sessionStorage.setItem('product_id', sku);
        sessionStorage.setItem('account_id', account_id);
        // Clean the URL by removing the token parameter
        this.cleanUrlParameter();
        this.editable = false;
        this.errorMessage = '';
        this.type = 'aws';
        
      } else {
        // Check if token exists in sessionStorage (for page refreshes)
        const storedToken = sessionStorage.getItem('x-amzn-marketplace-token');
        const storedSku = sessionStorage.getItem('product_id');
        if (storedToken && storedSku) {
          this.formData.mkt_token = storedToken;
          this.formData.sku = storedSku;
          this.errorMessage = '';
          this.editable = false;
          this.type = 'aws';
        } else if (!loggedIn) {
          this.type = 'aws';
          this.errorMessage = 'Missing required marketplace token. Please ensure you are accessing this page through the AWS Marketplace.';
        }
      }
    },
    cleanUrlParameter() {
      // Remove token from URL for security and cleanliness
      const url = new URL(window.location);
      url.searchParams.delete('token');
      url.searchParams.delete('x-amzn-marketplace-token');
      url.searchParams.delete('product_id');
      url.searchParams.delete('account_id');
      // Update URL without refreshing the page
      window.history.replaceState({}, document.title, url.pathname + url.search);
    },

    async nextStep() {
      let valid = true;
      if (this.currentStep === 1 && this.existCust && this.cid) {
        valid = await this.validateCustomerID();
      }
      if (this.currentStep === 2 && this.formData.mkt_token) {
        valid = await this.validateProductID();
      }
      if (valid && this.canProceedToNextStep && this.currentStep <= 2 && this.errorMessage === '') {
        this.currentStep++;
        this.errorMessage = ''; // Clear any errors when moving to next step
        this.initializeTooltips(); // Reinitialize tooltips for new step
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.errorMessage = ''; // Clear any errors when going back
        this.initializeTooltips(); // Reinitialize tooltips for new step
      }
    },
    async fetchTemplate() {
      if (this.templateLoading || !this.templateUrl) return;
      this.templateLoading = true;
      this.templateError = '';
      try {
        // Decode the URL before fetching
        const response = await fetch(this.templateUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        this.fetchedTemplate = await response.text();
      } catch (error) {
        this.templateError = `Unable to load template: ${error.message}`;
        this.fetchedTemplate = '# Template loading failed\n# Please try again or contact support';
      } finally {
        this.templateLoading = false;
      }
    },
    async submitForm() {
      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        // Prepare the data for submission
        const submissionData = {
          email: this.formData.email,
          organization: this.orgName,
          cid: this.existCust ? this.cid : null,
          mkt_token: this.formData.mkt_token || null,
          sku: this.formData.sku,
          ...this.formData.sku_info
        };
        if(this.type === 'aws'){
        // Make API call to onboarding endpoint
          await axios.post(`${this.$store.state.clientMgmtUrl}/v1/marketplace/customer/onboarding`, submissionData, {
            headers: {
              'Content-Type': 'application/json', ...this.getHeaders()
            }
          }).then((res) => {
            this.isSubmitted = true;
            sessionStorage.clear();
            this.currentStep++;
          })
        }else if (this.type === 'aqfer'){
          // Make API call to onboarding endpoint
          await axios.post(`${this.$store.state.clientMgmtUrl}/v1/customer/onboarding`, submissionData, {
            headers: {
              'Content-Type': 'application/json', ...this.getHeaders()
            }
          }).then((res) => {
            this.isSubmitted = true;
            sessionStorage.clear();
            this.currentStep++;
          })
        }
      } catch (error) {
        this.errorMessage = error.message || 'An error occurred during onboarding. Please try again.';
      } finally {
        this.isSubmitting = false;
        sessionStorage.clear();
      }
    },
    async validateCustomerID() {
      if (!this.cid) return;
      try {
        const response = await axios.get(`${this.$store.state.clientMgmtUrl}/v1/cids/${this.cid}?organization=${this.orgName}`, {
          headers: this.getHeaders(),
        })
        if (response.data) {
          this.errorMessage = '';
        } else {
          this.errorMessage = response.message || 'Invalid Customer ID';
        }
        return response.data;
      } catch (error) {
        this.errorMessage = 'Invalid Customer ID or Organization Name. Please try again.';
      }
    },
    async validateProductID() {
      if (!this.formData.sku) return;
      try {
        const response = await axios.get(`${this.$store.state.clientMgmtUrl}/v1/marketplace/products?pid=${this.formData.sku}`, {
          headers: this.getHeaders(),
        })
        if (response.data && response.data.length > 0) {
          this.errorMessage = '';
        } else {
          this.errorMessage = response.message || 'Invalid Product ID';
        }
        return response.data;
      } catch (error) {
        this.errorMessage = error.response.data.message || 'Error validating Product ID. Please try again.';
      }
    },
    async checkLoggedIn() {
      return this.$store.dispatch('whoAmICheck').then(res => {
        if (res && res.data?.email) {
          return true;
        }
        return false; 
      }).catch(err => {
        return false;
      })
    },
    getHeaders() {
      const headers = {};
      const accesstoken = this.$store.state.accessToken;
      const token = this.formData.mkt_token || sessionStorage.getItem('x-amzn-marketplace-token');
      if (token) {
        headers['mkt_token'] = `${token}`;
      } else if (accesstoken) {
        headers['Authorization'] = `Bearer ${accesstoken}`;
      }
      return headers;
    },
    async fetchSKUList(){
       try {
        await axios.get(`${this.$store.state.clientMgmtUrl}/v1/skus?marketplace=${this.type}`, {
            headers: {
              ...this.getHeaders()
            }
          }).then((res) => {
            this.skuList = res?.data || []
          })
        
      } catch (error) {
        this.errorMessage = error.message || 'An error occurred during onboarding. Please try again.';
      }
    },
    // Debounced version of fetchCFM to prevent excessive API calls
    debouncedFetchCFM: debounce(function(region) {
      this.fetchCFM(region);
    }, 500),
    async fetchCFM(region){
      this.templateLoading = true;
       try {
        let payload ={
          name:this.formData.sku
        }
        if(region)
          payload.parameters={region}
        await axios.post(`${this.$store.state.clientMgmtUrl}/v1/onboarding/cfm`,payload,{
            headers: {
              'Content-Type': 'application/json', ...this.getHeaders()
            }
          }).then((res) => {
            this.templateUrl = res.data.template
            if (region)
              this.templateRedirectUrl = res.data.url
            this.fetchTemplate()
          })
        
      } catch (error) {
        const errMsg = error.response?.data?.error || '';
        const noTemplate = error.response?.status === 404 ||
          errMsg.includes('invalid input') ||
          errMsg.includes('record not found') ||
          errMsg.includes('CFM not found');
        if (noTemplate) {
          this.noTemplate = true;
          this.cfmCheckComplete = true;
        } else {
          this.errorMessage = 'Unable to fetch CFM template. Please try again or contact support.';
        }
      } finally{
          this.templateLoading = false
      }
    },
    getOptions(val){
      return val.map(item=>{ return {label:item,value:item}})
    },
    async onCopyCFM(){     
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(this.templateRedirectUrl || this.currentTemplate)
            } else {
                const input = document.createElement('textarea')
                input.style = 'position: absolute; left: -1000px; top: -1000px';
                input.value = this.templateRedirectUrl || this.currentTemplate;
                document.body.appendChild(input)
                input.select()
                input.setSelectionRange(0, 99999); 
                document.execCommand('copy')
                document.body.removeChild(input)
            }    
            const options = {
                message: this.templateRedirectUrl ?"CFM template URL copied to clipboard." : "CFM template copied to clipboard.",
                type: "info"
            }
            this.$store.dispatch('addToast', options);        
            
    },
    async viewCFMTemplate() {
      this.templateError = '';
      this.showCFMModal = true;
      // Show modal immediately if template already loaded
      if (!this.fetchedTemplate) {
        await this.fetchTemplate();
      }
    },
    closeCFMModal() {
      this.showCFMModal = false;
    },
  }
};
</script>

<style lang="scss" scoped>
@use '../theme/scss/user-variables' as *;
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

.step-content {
  min-height: 400px;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-weight: bold;
  transition: all 0.3s ease;

  &.active {
    background-color: #007bff;
    border-color: #007bff;
    color: white;
  }

  &.completed {
    background-color: #28a745;
    border-color: #28a745;
    color: white;
  }
}

.step-line {
  width: 60px;
  height: 2px;
  background-color: #dee2e6;
  margin: 0 10px;
  transition: all 0.3s ease;

  &.active {
    background-color: #28a745;
  }
}

.form-label i[data-toggle="tooltip"] {
  font-size: 0.875rem;
  cursor: help;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}

.alert-info-bg {
  background-color: #c0dbff;
  border-color: #b6e0f9;
  color: #1c5b7a;
}

.onboarding-container :deep(.form-group) {
  margin-bottom: 0.8rem !important;
}

/* Two-column layout for form fields when there are more than 3 fields */
.sku-config-fields.two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  align-items: start;
}

.sku-config-fields.two-columns :deep(.form-group) {
  margin-bottom: 0.8rem !important;
}

body.dark-mode {
  .alert-light {
    background-color: $dark-card-bg;
    border-color: $dark-border-color;
    color: $dark-text-primary;
  }

  .alert-info-bg {
    background-color: rgba(0, 110, 255, 0.1);
    border-color: rgba(0, 110, 255, 0.1);
    color: #b6e0f9;
  }
}
</style>